import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Hash, Home, Users, MessageCircle, Plus, ArrowLeft, Send, Paperclip, BarChart3, BellOff, Bell,
  LogOut, Trash2, EyeOff, Settings, Mail, UserPlus, X, Search,
} from 'lucide-react';
import { Button } from './ui/button';
import { shrinkImage } from './regSession';
import ChatAdminPanel from './ChatAdminPanel';
import CommBroadcastPanel from './comm/CommBroadcastPanel';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const ICON = { team: Hash, family: Home, group: Users, direct: MessageCircle };
const when = (v) => (v ? new Date(v).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const errText = (e, fb) => { const d = e?.response?.data?.detail; return (d && (d.message || d)) || fb; };

/**
 * Communications ▸ Messaging (AD + Scheduler, AJ 2026-10-16) — the SAME chats members use in the club app
 * (docs/OL_ACCESS_LINKS.md §10/§14): team chats (join the ones you want), groups, direct messages, photos/PDF,
 * polls, mute, leave. Club controls (chat on/off, direct messages on/off, reported messages) under Settings.
 * Calls OL's chat API with the staff cookie + X-OL-Org; staff remove any message for everyone.
 */
export default function ClubChatPanel({ orgId }) {
  const H = useMemo(() => ({ withCredentials: true, headers: { 'X-OL-Org': orgId } }), [orgId]);
  const chat = useCallback((m, p, data, params) => axios({ method: m, url: `${API}/sa/v1/chat${p}`, data, params, ...H }).then(r => r.data), [H]);
  const [list, setList] = useState(null);
  const [sport, setSport] = useState('');
  const [sort, setSort] = useState('recent');       // recent | az | unread
  const [open, setOpen] = useState(null);           // channel
  const [view, setView] = useState('list');          // list | chat | dm | group | settings | email | people
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try { setList(await chat('get', '/channels')); setErr(''); }
    catch (e) { setErr(errText(e, 'Could not load chats')); setList({ channels: [], joinable: [], settings: {} }); }
  }, [chat]);
  useEffect(() => { if (orgId) load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [orgId, load]);

  const sports = useMemo(() => {
    const m = new Map();
    [...(list?.channels || []), ...(list?.joinable || [])].forEach(c => { if (c.sport) m.set(c.sport.slug, c.sport.title); });
    return [...m.entries()];
  }, [list]);
  const bySport = (c) => !sport || !c.sport || c.sport.slug === sport;

  const join = async (c) => { try { await chat('post', `/channels/${c.id}/join`, {}); toast.success(`Joined ${c.name}`); load(); } catch (e) { toast.error(errText(e, 'Could not join')); } };
  const go = (c) => { setOpen(c); setView('chat'); };

  if (!list) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (view === 'chat' && open) return <ChatView chat={chat} orgId={orgId} ch={open} admin={!!list.chat_admin} onBack={() => { setView('list'); setOpen(null); load(); }} onPeople={() => setView('people')} />;
  if (view === 'people' && open) return <GroupPeople chat={chat} ch={open} admin={!!list.chat_admin} onBack={() => setView('chat')} />;
  if (view === 'dm') return <NewDirect chat={chat} onBack={() => setView('list')} onOpen={(id) => { load(); go({ id, name: 'Direct message', type: 'direct', can_post: true }); }} />;
  if (view === 'group') return <NewGroup chat={chat} staff={!!list.chat_admin} teams={(list.channels || []).filter(c => c.type === 'team' && c.can_post)} onBack={() => setView('list')} onOpen={(id, name) => { load(); go({ id, name, type: 'group', can_post: true }); }} />;
  const back = <Button size="sm" variant="ghost" onClick={() => setView('list')}><ArrowLeft className="h-4 w-4 mr-1" />Back to chats</Button>;
  if (view === 'settings') return <div className="space-y-3">{back}<ChatAdminPanel orgId={orgId} /></div>;
  if (view === 'email') return <div className="space-y-3">{back}<CommBroadcastPanel orgId={orgId} /></div>;

  const order = {
    recent: (a, b) => (b.last_message_at || '').localeCompare(a.last_message_at || ''),
    az: (a, b) => (a.name || '').localeCompare(b.name || ''),
    unread: (a, b) => (b.unread || 0) - (a.unread || 0) || (b.last_message_at || '').localeCompare(a.last_message_at || ''),
  }[sort];
  const shown = (list.channels || []).filter(bySport).sort(order);
  const joinable = (list.joinable || []).filter(bySport);
  const dmOn = list.settings?.direct_messages !== false;
  return (
    <div className="rounded-xl border bg-white dark:bg-slate-900 dark:border-slate-700 p-4 space-y-3" data-testid="club-chat">
      <div className="flex flex-wrap items-center gap-2">
        <MessageCircle className="h-5 w-5 text-sky-600" /><h3 className="font-semibold">Messaging</h3>
        <span className="text-xs text-slate-500">the club app's chats — members see your messages on their phones</span>
        <select className="h-8 text-sm rounded border px-2 bg-white dark:bg-slate-900 dark:border-slate-600 ml-auto" value={sport} onChange={e => setSport(e.target.value)} data-testid="chat-sport">
          <option value="">All sports</option>{sports.map(([s, t]) => <option key={s} value={s}>{t}</option>)}
        </select>
        <select className="h-8 text-sm rounded border px-2 bg-white dark:bg-slate-900 dark:border-slate-600" value={sort} onChange={e => setSort(e.target.value)} data-testid="chat-sort">
          <option value="recent">Most recent</option><option value="unread">Unread first</option><option value="az">A–Z</option>
        </select>
        {dmOn && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setView('dm')} data-testid="chat-new-dm"><Plus className="h-3.5 w-3.5 mr-1" />New message</Button>}
        {dmOn && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setView('group')} data-testid="chat-new-group"><Users className="h-3.5 w-3.5 mr-1" />New group</Button>}
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setView('email')} data-testid="chat-email"><Mail className="h-3.5 w-3.5 mr-1" />Email broadcast</Button>
        {list.chat_admin && <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setView('settings')} data-testid="chat-settings"><Settings className="h-3.5 w-3.5 mr-1" />Club chat settings</Button>}
      </div>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <div className="flex flex-wrap gap-2 text-xs" data-testid="chat-status">
        <span className={`rounded px-2 py-0.5 ${list.settings?.enabled === false ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>Chat {list.settings?.enabled === false ? 'off' : 'on'}</span>
        <span className={`rounded px-2 py-0.5 ${dmOn ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>Direct + group messages {dmOn ? 'on' : 'off'}</span>
        {list.chat_admin && <button className="underline text-slate-500" onClick={() => setView('settings')}>change</button>}
      </div>
      {list.settings?.enabled === false && <p className="text-sm text-amber-700">Chat is turned off for the club — turn it on under Club chat settings.</p>}
      <div className="rounded border divide-y dark:border-slate-700 dark:divide-slate-700">
        {shown.length === 0 && <p className="p-3 text-sm text-slate-500">No chats yet{joinable.length ? ' — join a team chat below.' : '.'}</p>}
        {shown.map(c => {
          const I = ICON[c.type] || Hash;
          return (
            <button key={c.id} onClick={() => go(c)} className="w-full text-left p-3 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`chat-row-${c.id}`}>
              <I className="h-4 w-4 text-slate-500 shrink-0" />
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{c.name}</div>
                <div className="text-xs text-slate-500">{c.type}{c.sport ? ` · ${c.sport.title}` : ''}{c.post_policy === 'staff' ? ' · staff post only' : c.post_policy === 'closed' ? ' · closed' : (c.read_only ? ' · read only' : '')}{c.last_message_at ? ` · ${when(c.last_message_at)}` : ''}</div></div>
              {c.muted && <BellOff className="h-4 w-4 text-slate-400" />}
              {c.unread > 0 && <span className="text-xs rounded-full bg-sky-600 text-white px-2 py-0.5">{c.unread}</span>}
            </button>
          );
        })}
      </div>
      {joinable.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-500 mb-1">Team chats you can join</div>
          <div className="flex flex-wrap gap-2">
            {joinable.map(c => <Button key={c.id} size="sm" variant="outline" className="h-8 text-xs" onClick={() => join(c)} data-testid={`chat-join-${c.id}`}><Plus className="h-3.5 w-3.5 mr-1" />{c.name}</Button>)}
          </div>
        </div>
      )}
    </div>
  );
}

function ChatView({ chat, orgId, ch, admin, onBack, onPeople }) {
  const [policy, setPolicy] = useState(ch.post_policy || 'all');
  const [msgs, setMsgs] = useState([]);
  const [canPost, setCanPost] = useState(!!ch.can_post);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [poll, setPoll] = useState(null);
  const [muted, setMuted] = useState(!!ch.muted);
  const lastRef = useRef('');
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const merge = (rows) => setMsgs(prev => {
    const m = new Map(prev.map(x => [x.id, x]));
    rows.forEach(x => m.set(x.id, x));
    return [...m.values()].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
  });
  const pull = useCallback(async (first) => {
    try {
      const d = await chat('get', `/channels/${ch.id}/messages`, undefined, first ? { limit: 100 } : { after: lastRef.current, limit: 100 });
      setCanPost(d.can_post);
      if (d.messages.length) {
        lastRef.current = d.messages.reduce((a, m) => (m.created_at > a ? m.created_at : a), lastRef.current);
        if (first) setMsgs(d.messages); else merge(d.messages);
      }
    } catch (e) { if (first) toast.error(errText(e, 'Could not open the chat')); }
  }, [chat, ch.id]);
  useEffect(() => { pull(true); const t = setInterval(() => pull(false), 10000); return () => clearInterval(t); }, [pull]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs.length]);

  const send = async (body) => {
    setBusy(true);
    try { const d = await chat('post', `/channels/${ch.id}/messages`, body); merge([d.message]); lastRef.current = d.message.created_at > lastRef.current ? d.message.created_at : lastRef.current; setText(''); setPoll(null); }
    catch (e) { toast.error(errText(e, 'Could not send')); }
    finally { setBusy(false); }
  };
  const attach = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const small = await shrinkImage(f);
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(',')[1]); r.onerror = rej; r.readAsDataURL(small); });
      const a = await chat('post', `/channels/${ch.id}/attachments`, { name: f.name, content_type: small.type || f.type, data_base64: b64 });
      await send({ text: text.trim(), attachment_ids: [a.attachment_id] });
    } catch (e) { toast.error(errText(e, 'Could not send the file')); setBusy(false); }
  };
  const vote = async (m, id) => {
    const cur = m.poll.my_votes || [];
    const next = m.poll.multi ? (cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]) : (cur.includes(id) ? [] : [id]);
    try { const d = await chat('post', `/messages/${m.id}/vote`, { option_ids: next }); merge([{ ...m, poll: d.poll }]); }
    catch (e) { toast.error(errText(e, 'Could not vote')); }
  };
  const del = async (m) => { if (!window.confirm('Delete your message?')) return; try { await chat('delete', `/messages/${m.id}`); merge([{ ...m, deleted: true, text: null, attachments: [], poll: null }]); } catch (e) { toast.error(errText(e, 'Could not delete')); } };
  const hide = async (m) => {
    if (!window.confirm('Remove this message for everyone?')) return;
    try { await axios.post(`${API}/chat-admin/${orgId}/messages/${m.id}/hide`, { hidden: true }, { withCredentials: true }); setMsgs(p => p.filter(x => x.id !== m.id)); toast.success('Removed'); }
    catch (e) { toast.error(errText(e, 'Could not remove')); }
  };
  const closePoll = async (m) => { try { await chat('post', `/messages/${m.id}/close-poll`, {}); merge([{ ...m, poll: { ...m.poll, closed: true } }]); } catch (e) { toast.error(errText(e, 'Could not close')); } };
  const mute = async () => { try { const d = await chat('post', `/channels/${ch.id}/mute`, { muted: !muted }); setMuted(d.muted); } catch (e) { toast.error(errText(e, 'Could not change')); } };
  const leave = async () => { if (!window.confirm(`Leave ${ch.name}?`)) return; try { await chat('post', `/channels/${ch.id}/leave`, {}); onBack(); } catch (e) { toast.error(errText(e, 'Could not leave')); } };
  const canLeave = ch.type === 'group' || (ch.type === 'team' && ch.joined_as_staff);
  const changePolicy = async (v) => {
    try { const d = await chat('post', `/channels/${ch.id}/policy`, { post_policy: v }); setPolicy(d.post_policy); toast.success('Saved'); pull(true); }
    catch (e) { toast.error(errText(e, 'Could not change')); }
  };

  return (
    <div className="rounded-xl border bg-white dark:bg-slate-900 dark:border-slate-700 flex flex-col" style={{ height: '70vh' }} data-testid="club-chat-view">
      <div className="flex items-center gap-2 p-3 border-b dark:border-slate-700">
        <Button size="sm" variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="font-semibold truncate flex-1">{ch.name}</div>
        {admin && (ch.type === 'team' || ch.type === 'group') && (
          <label className="text-xs text-slate-500 flex items-center gap-1">Who can post
            <select className="h-7 text-xs rounded border px-1 bg-white dark:bg-slate-900 dark:border-slate-600" value={policy} onChange={e => changePolicy(e.target.value)} data-testid="chat-policy">
              <option value="all">Everyone</option><option value="staff">Staff only</option><option value="closed">Nobody (closed)</option>
            </select></label>
        )}
        {ch.type === 'group' && <Button size="sm" variant="ghost" className="text-xs" onClick={onPeople} data-testid="chat-people"><Users className="h-4 w-4 mr-1" />People</Button>}
        <Button size="sm" variant="ghost" className="text-xs" onClick={mute} title={muted ? 'Turn alerts back on' : 'Mute'}>{muted ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}</Button>
        {canLeave && <Button size="sm" variant="ghost" className="text-xs text-red-600" onClick={leave} data-testid="chat-leave"><LogOut className="h-4 w-4 mr-1" />Leave</Button>}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {msgs.length === 0 && <p className="text-sm text-slate-500">No messages yet.</p>}
        {msgs.map(m => (
          <div key={m.id} className={`flex ${m.mine ? 'justify-end' : ''}`}>
            <div className={`group max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.mine ? 'bg-sky-600 text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>
              <div className={`text-[11px] mb-0.5 ${m.mine ? 'text-sky-100' : 'text-slate-500'}`}>{m.sender?.name} · {when(m.created_at)}</div>
              {m.deleted ? <i className="opacity-70">message deleted</i> : <>
                {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                {(m.attachments || []).map(a => a.content_type?.startsWith('image/')
                  ? <a key={a.id} href={a.url} target="_blank" rel="noreferrer"><img src={a.url} alt={a.name} className="mt-1 max-h-56 rounded" /></a>
                  : <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="block underline mt-1">📎 {a.name}</a>)}
                {m.poll && (
                  <div className="mt-1 space-y-1" data-testid={`poll-${m.id}`}>
                    <div className="font-semibold">📊 {m.poll.question}{m.poll.closed ? ' (closed)' : ''}</div>
                    {m.poll.options.map(o => (
                      <button key={o.id} disabled={m.poll.closed || !canPost} onClick={() => vote(m, o.id)}
                        className={`w-full text-left rounded border px-2 py-1 ${(m.poll.my_votes || []).includes(o.id) ? 'border-amber-400 font-semibold' : 'border-slate-300 dark:border-slate-600'}`}>
                        {o.text} <span className="float-right">{o.votes}</span></button>
                    ))}
                    <div className="text-[11px] opacity-80">{m.poll.voters} voted{m.poll.multi ? ' · several choices allowed' : ''}
                      {m.mine && !m.poll.closed && <button className="underline ml-2" onClick={() => closePoll(m)}>Close poll</button>}</div>
                  </div>
                )}
                <div className="hidden group-hover:flex gap-2 mt-1 text-[11px]">
                  {m.mine && <button className="underline" onClick={() => del(m)}><Trash2 className="h-3 w-3 inline" /> Delete</button>}
                  {!m.mine && <button className="underline" onClick={() => hide(m)}><EyeOff className="h-3 w-3 inline" /> Remove for everyone</button>}
                </div>
              </>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      {canPost ? (
        <div className="border-t p-2 space-y-2 dark:border-slate-700">
          {poll && (
            <div className="rounded border p-2 space-y-1 dark:border-slate-600" data-testid="poll-form">
              <input className="w-full h-8 rounded border px-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-600" placeholder="Question" value={poll.question} onChange={e => setPoll({ ...poll, question: e.target.value })} />
              {poll.options.map((o, i) => <input key={i} className="w-full h-8 rounded border px-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-600" placeholder={`Choice ${i + 1}`} value={o}
                onChange={e => setPoll({ ...poll, options: poll.options.map((x, j) => (j === i ? e.target.value : x)) })} />)}
              <div className="flex items-center gap-3 text-xs">
                {poll.options.length < 10 && <button className="underline" onClick={() => setPoll({ ...poll, options: [...poll.options, ''] })}>+ choice</button>}
                <label className="flex items-center gap-1"><input type="checkbox" checked={poll.multi} onChange={e => setPoll({ ...poll, multi: e.target.checked })} />allow several</label>
                <Button size="sm" className="h-7 text-xs ml-auto" disabled={busy} onClick={() => send({ poll: { ...poll, options: poll.options.filter(x => x.trim()) } })}>Post poll</Button>
                <button onClick={() => setPoll(null)} aria-label="Cancel poll"><X className="h-4 w-4" /></button>
              </div>
            </div>
          )}
          <div className="flex items-end gap-2">
            <button title="Photo or PDF" onClick={() => fileRef.current?.click()} className="p-2 text-slate-500" disabled={busy}><Paperclip className="h-4 w-4" /></button>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={e => { attach(e.target.files?.[0]); e.target.value = ''; }} />
            <button title="Poll" onClick={() => setPoll(poll ? null : { question: '', options: ['', ''], multi: false })} className="p-2 text-slate-500"><BarChart3 className="h-4 w-4" /></button>
            <textarea rows={1} className="flex-1 rounded border px-2 py-1.5 text-sm resize-none bg-white dark:bg-slate-900 dark:border-slate-600" placeholder="Message" value={text}
              onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && text.trim()) { e.preventDefault(); send({ text: text.trim() }); } }} data-testid="chat-input" />
            <Button size="sm" disabled={busy || !text.trim()} onClick={() => send({ text: text.trim() })} data-testid="chat-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
          </div>
        </div>
      ) : <p className="border-t p-3 text-xs text-slate-500 dark:border-slate-700">Read only.</p>}
    </div>
  );
}

function PeoplePicker({ chat, team, picked, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = {}; if (team) params.team = team; if (q.trim().length >= 2) params.q = q.trim();
      chat('get', '/people', undefined, params).then(d => setRows(d.people || [])).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, team, chat]);
  return (
    <div className="space-y-2">
      <div className="relative"><Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
        <input className="w-full h-8 rounded border pl-7 pr-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-600" placeholder="Search by first or last name (2+ letters)" value={q} onChange={e => setQ(e.target.value)} data-testid="people-q" /></div>
      <div className="rounded border divide-y max-h-72 overflow-y-auto dark:border-slate-700 dark:divide-slate-700">
        {rows.length === 0 && <p className="p-2 text-xs text-slate-500">Nobody found.</p>}
        {rows.map(p => (
          <button key={p.id} onClick={() => onPick(p)} className={`w-full text-left p-2 text-sm flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800 ${picked?.has?.(p.id) ? 'bg-sky-50 dark:bg-sky-900/30' : ''}`}>
            <span className="flex-1">{p.full_name || p.name}</span><span className="text-[11px] text-slate-500 capitalize">{p.kind}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function NewDirect({ chat, onBack, onOpen }) {
  const start = async (p) => { try { const d = await chat('post', '/direct', { person_id: p.id }); onOpen(d.channel_id); } catch (e) { toast.error(errText(e, 'Could not start the chat')); } };
  return (
    <div className="rounded-xl border bg-white dark:bg-slate-900 dark:border-slate-700 p-4 space-y-3 max-w-lg">
      <Button size="sm" variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
      <h3 className="font-semibold">New message</h3>
      <p className="text-xs text-slate-500">Adults in the club only. Empty search shows staff.</p>
      <PeoplePicker chat={chat} onPick={start} />
    </div>
  );
}

function NewGroup({ chat, staff, teams, onBack, onOpen }) {
  const [team, setTeam] = useState(staff ? '' : (teams[0]?.team_slug || ''));
  const [name, setName] = useState('');
  const [picked, setPicked] = useState(new Map());
  const toggle = (p) => setPicked(m => { const n = new Map(m); if (n.has(p.id)) n.delete(p.id); else n.set(p.id, p); return n; });
  const create = async () => {
    try { const d = await chat('post', '/groups', { name, team_slug: team, person_ids: [...picked.keys()] }); onOpen(d.channel_id, name); }
    catch (e) { toast.error(errText(e, 'Could not create the group')); }
  };
  return (
    <div className="rounded-xl border bg-white dark:bg-slate-900 dark:border-slate-700 p-4 space-y-3 max-w-lg">
      <Button size="sm" variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
      <h3 className="font-semibold">New group</h3>
      {teams.length === 0 && !staff ? <p className="text-sm text-slate-500">Join a team chat first — a group belongs to one team.</p> : <>
        <select className="w-full h-8 rounded border px-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-600" value={team} onChange={e => { setTeam(e.target.value); setPicked(new Map()); }} data-testid="group-team">
          {staff && <option value="">Any club members (no team)</option>}
          {teams.map(t => <option key={t.id} value={t.team_slug}>{t.name}</option>)}
        </select>
        <input className="w-full h-8 rounded border px-2 text-sm bg-white dark:bg-slate-900 dark:border-slate-600" placeholder="Group name" value={name} onChange={e => setName(e.target.value)} data-testid="group-name" />
        <p className="text-xs text-slate-500">{team ? 'Everyone must be an adult on that team.' : 'Search any adult member of the club.'} {picked.size} picked.</p>
        {picked.size > 0 && <div className="flex flex-wrap gap-1">{[...picked.values()].map(p => <span key={p.id} className="text-xs rounded bg-sky-100 dark:bg-sky-900/40 px-2 py-0.5">{p.full_name || p.name} <button onClick={() => toggle(p)} aria-label="Remove">×</button></span>)}</div>}
        <PeoplePicker chat={chat} team={team} picked={picked} onPick={toggle} />
        <Button size="sm" disabled={!name.trim()} onClick={create} data-testid="group-create"><UserPlus className="h-4 w-4 mr-1" />Create group</Button>
      </>}
    </div>
  );
}

function GroupPeople({ chat, ch, admin, onBack }) {
  const [g, setG] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => chat('get', `/groups/${ch.id}`).then(setG).catch(e => toast.error(errText(e, 'Could not load'))), [chat, ch.id]);
  useEffect(() => { load(); }, [load]);
  const change = async (body) => { try { await chat('post', `/groups/${ch.id}/members`, body); load(); } catch (e) { toast.error(errText(e, 'Could not change')); } };
  if (!g) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <div className="rounded-xl border bg-white dark:bg-slate-900 dark:border-slate-700 p-4 space-y-3 max-w-lg">
      <Button size="sm" variant="ghost" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Back to {g.name}</Button>
      <h3 className="font-semibold">People ({g.members.length})</h3>
      <div className="rounded border divide-y dark:border-slate-700 dark:divide-slate-700">
        {g.members.map(m => (
          <div key={m.id} className="p-2 text-sm flex items-center gap-2">
            <span className="flex-1">{m.full_name}{m.me ? ' (you)' : ''}{m.started_group ? ' · started the group' : ''}</span>
            <span className="text-[11px] text-slate-500 capitalize">{m.kind}</span>
            {(g.created_by_me || admin) && !m.me && <button className="text-xs text-red-600 underline" onClick={() => change({ remove: [m.id] })}>Remove</button>}
          </div>
        ))}
      </div>
      {adding ? <PeoplePicker chat={chat} team={g.team_slug} onPick={(p) => change({ add: [p.id] })} />
        : <Button size="sm" variant="outline" onClick={() => setAdding(true)}><UserPlus className="h-4 w-4 mr-1" />Add people</Button>}
    </div>
  );
}
